import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Icon from '@mui/material/Icon'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import React from 'react'

import Loader from '@/app/component/atom/loader'
import SearchBar from '@/app/component/atom/search'
import TablePagination from '@/app/component/atom/table-pagination'
import Tooltip from '@/app/component/atom/tooltip'
import { SELECT_CONTACTS_INFO_TOOLTIP } from '@/app/module/campaigns/definitions'
import { type TablePagination as TablePaginationType } from '@/app/types'

type Props = {
  actions: React.ReactNode
  children: React.ReactNode
  emptyMessage: React.ReactNode
  filter?: React.ReactNode
  isLoading: boolean
  pageSize: number
  pagination: TablePaginationType
  refreshDisabled: boolean
  refreshLabel: string
  searchLabel: string
  searchTerm?: string | null
  selectedCount: number

  onClearSelection: () => void
  onRefresh: () => void
  onSearch: (searchTerm: string) => void
}

const MobileSubscriptionsLayout: React.FC<Props> = (props) => {
  const {
    actions,
    children,
    emptyMessage,
    filter,
    isLoading,
    pageSize,
    pagination,
    refreshDisabled,
    refreshLabel,
    searchLabel,
    searchTerm,
    selectedCount,
    onClearSelection,
    onRefresh,
    onSearch,
  } = props
  const actionsRef = React.useRef<HTMLDivElement>(null)
  const [actionsHeight, setActionsHeight] = React.useState(0)

  React.useLayoutEffect(() => {
    const actionsElement = actionsRef.current

    if (!actionsElement || selectedCount === 0) {
      setActionsHeight(0)
      return
    }

    const updateActionsHeight = () => {
      setActionsHeight(Math.ceil(actionsElement.getBoundingClientRect().height))
    }

    updateActionsHeight()

    if (typeof ResizeObserver === 'undefined') {
      return
    }

    const resizeObserver = new ResizeObserver(updateActionsHeight)
    resizeObserver.observe(actionsElement)

    return () => resizeObserver.disconnect()
  }, [selectedCount])

  return (
    <Box sx={{ minHeight: '100%', minWidth: 0, overflowX: 'clip', width: '100%' }}>
      <Box sx={{ paddingY: 2, paddingBottom: 0 }}>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexWrap: 'nowrap',
            gap: 1,
            justifyContent: 'flex-end',
            minWidth: 0,
            '& > :not(form)': {
              flex: '0 0 auto',
            },
          }}
        >
          <SearchBar
            disabled={isLoading}
            expandable
            label={searchLabel}
            namespace="campaign-manage-subscriptions"
            searchTerm={searchTerm}
            onSubmit={onSearch}
          />
          {filter}
          <Tooltip title={refreshLabel}>
            <span>
              <IconButton aria-label={refreshLabel} disabled={refreshDisabled} onClick={onRefresh}>
                <Icon>refresh</Icon>
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {selectedCount > 0 && (
        <Paper
          ref={actionsRef}
          square
          variant="outlined"
          sx={{
            alignItems: 'center',
            backgroundColor: 'background.paper',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            padding: 1.5,
            bottom: 0,
            boxSizing: 'border-box',
            left: 0,
            maxWidth: '100vw',
            position: 'fixed',
            right: 0,
            zIndex: (theme) => theme.zIndex.appBar - 1,
          }}
        >
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, minWidth: 0 }}>
            <Typography color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
              {selectedCount} selected
            </Typography>
            <Tooltip title={SELECT_CONTACTS_INFO_TOOLTIP}>
              <Box component="span" sx={{ display: 'inline-flex' }} tabIndex={0}>
                <Icon aria-label="Select contacts info" color="action" fontSize="small">
                  info_outline
                </Icon>
              </Box>
            </Tooltip>
            <Button size="small" variant="text" onClick={onClearSelection}>
              Clear
            </Button>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flex: '1 1 100%',
              flexWrap: 'nowrap',
              gap: 1,
              minWidth: 0,
              '& > *': {
                flex: '1 1 0',
                minWidth: 0,
              },
              '& .MuiButton-root': {
                minWidth: 0,
                paddingX: 0.5,
                whiteSpace: 'nowrap',
                width: '100%',
              },
            }}
          >
            {actions}
          </Box>
        </Paper>
      )}

      <Box sx={{ minHeight: isLoading ? 120 : 0, position: 'relative' }}>
        {!isLoading && pageSize === 0 ? (
          <Paper square>
            <Typography color="textSecondary" variant="h6" align="center" sx={{ padding: '3rem 1rem' }}>
              {emptyMessage}
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'grid', gap: 1.5, padding: '8px 0 16px' }}>{children}</Box>
        )}
        {isLoading && <Loader />}
      </Box>

      {pageSize > 0 && (
        <Box
          sx={{
            maxWidth: '100%',
            overflow: 'hidden',
            '& [data-testid^="table-pagination-"]': {
              boxSizing: 'border-box',
              paddingLeft: 0,
              paddingRight: 0,
            },
          }}
        >
          <TablePagination {...pagination} />
        </Box>
      )}
      <Divider />
      {selectedCount > 0 && <Box aria-hidden sx={{ height: actionsHeight }} />}
    </Box>
  )
}

export default MobileSubscriptionsLayout
